<script setup lang="ts">
import { useLocationStore } from '@/stores/useLocationStore'
import DropdownComponent from './DropdownComponent.vue'
import { slovakRegions } from '@/composables/useMapData'
import { useNormalizedFilter } from '@/composables/useNormalizedFilter'
import { useMediaQuery } from '@vueuse/core'


const isLargeScreen = useMediaQuery('(min-width: 1024px)')
const locationStore = useLocationStore()
const {
  filteredItems: filteredRegions,
  setSearchQuery,
  clearSearchQuery,
} = useNormalizedFilter(
  () => locationStore.regions,
  (region) => region,
)

function selectRegion(regionName: string): void {
  locationStore.selectedRegion = locationStore.selectedRegion === regionName ? '' : regionName
}

function clearRegion(): void {
  locationStore.selectedRegion = ''
  clearSearchQuery()
}
</script>

<template>
  <div v-if="isLargeScreen" class="regions-map">
    <svg viewBox="0 0 700 340" aria-label="Mapa krajov Slovenska" role="img">
      <path
        v-for="region in slovakRegions"
        :key="region.id"
        :d="region.path"
        :data-region="region.label"
        :class="{ active: locationStore.selectedRegion === region.label }"
        tabindex="0"
        @click="selectRegion(region.label)"
        @keydown.enter.prevent="selectRegion(region.label)"
      />
      <text
        v-for="region in slovakRegions"
        :key="`label-${region.id}`"
        :x="region.textX"
        :y="region.textY"
        :class="{ active: locationStore.selectedRegion === region.label }"
      >
        {{ region.label }}
      </text>
    </svg>
  </div>

  <DropdownComponent
    v-if="!isLargeScreen"
    class="region-select"
    v-model="locationStore.selectedRegion"
    :options="filteredRegions"
    :loading="locationStore.regionsIsLoading"
    :onClear="clearRegion"
    placeholder="Vyberte kraj"
    aria-label="Vyberte kraj"
    @search-change="setSearchQuery"
  />
</template>

<style scoped>
.regions-map {
  width: 100%;
  margin-bottom: 1rem;
}
.region-select {
  width: 100%;
}

svg {
  width: 100%;
  height: auto;
}

path {
  fill: #ffffff;
  stroke: #334155;
  stroke-width: 1.2;
  cursor: pointer;
  transition: fill 0.2s ease;
}

path:hover,
path:focus,
path:focus-visible {
  fill: #ffd51e;
  outline: none;
}

path:focus {
  fill: #ffffff;
  outline: none;
}

path.active {
  fill: #e2b81d;
  outline: none;
}

text {
  fill: #0f172a;
  font-size: 10px;
  font-weight: 600;
  text-anchor: middle;
  dominant-baseline: middle;
  pointer-events: none;
  user-select: none;
}

text.active {
  fill: #212629;
}
</style>
